import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Phone } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { contactFormSchema, type ContactFormValues } from "@/lib/contactSchema";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import benPhoto from "@/assets/ben-personal-photo.webp";

const heroImage = "/images/hero-flood.webp";



export const HeroSection = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      zip: "",
      serviceType: "",
      message: ""
    }
  });

  const handleSubmit = async (data: ContactFormValues) => {
    setIsSubmitting(true);
    try {
      const { data: result, error } = await supabase.functions.invoke("contact-form", {
        body: {
          name: data.name,
          phone: data.phone,
          email: data.email || "",
          zip: data.zip,
          serviceType: data.serviceType,
          message: data.message,
          website: ""
        }
      });

      if (error) {
        toast.error("Submission failed. Please call 830-360-2600 for immediate help.");
        return;
      }

      if (result?.error) {
        toast.error(result.error);
        return;
      }

      toast.success("Message sent! We'll contact you shortly.");
      form.reset();
      navigate("/damage-assessment-contact");
    } catch {
      toast.error("Submission failed. Please call 830-360-2600 for immediate help.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      className="relative flex items-center overflow-hidden bg-wtr-blue py-16 md:py-20"
      aria-label="Water damage restoration in New Braunfels TX">
      
      <picture>
        <source
          media="(max-width: 768px)"
          srcSet="/images/hero-flood-mobile.webp"
          type="image/webp"
          width={824}
          height={638}
        />
        <img
          src={heroImage}
          alt=""
          role="presentation"
          width={1920}
          height={1486}
          fetchPriority="high"
          loading="eager"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover" />
      </picture>
      

      <div className="absolute inset-0 z-[1] bg-gradient-to-r from-wtr-blue/95 to-wtr-blue-dark/85" />

      <div className="relative z-[2] max-w-6xl mx-auto w-full px-6">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          {/* Left: Headline + CTA */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <img
                src={benPhoto}
                alt="Ben Jernigan, WTR Restoration owner"
                width={56}
                height={56}
                loading="eager"
                className="w-14 h-14 rounded-full object-cover border-2 border-white/80 shadow-md"
              />
              <div className="text-white/90 text-sm leading-tight">
                <div className="font-semibold text-white">Ben Jernigan — Owner</div>
                <div className="text-white/70">Answers your call personally, 24/7</div>
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 leading-[1.05] tracking-tight">
              Water Damage and Disaster Pros
            </h1>

            <p className="text-lg md:text-xl text-white/90 mb-6 leading-relaxed">
              On Call 24/7 For Water Damage Restoration in New Braunfels, Seguin, San Marcos and Central Texas 
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <Button
                variant="phone"
                size="lg"
                className="text-base px-6 py-3 h-auto"
                onClick={() => window.open('tel:830-360-2600')}>
                
                <Phone className="w-5 h-5" />
                Call Now: 830-360-2600
              </Button>
            </div>
            <p className="text-sm text-white/60 italic text-left">
              Insurance Preferred Water Damage Restoration Specialist<br />
              All Insurance Accepted
            </p>
            <p className="text-white/50 mt-1 text-base text-left">
              RIA • IICRC Certified • Direct Insurance Billing
            </p>
            <Link
              to="/60-minute-water-damage-survival-guide"
              className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-white/80 hover:text-white transition-colors underline underline-offset-2 text-center">
              
              📘 Read the 60-Minute Water Damage Survival Guide
            </Link>
          </div>

          {/* Right: Contact Form */}
          <div className="bg-white rounded-xl p-6 shadow-xl">
            <h2 className="text-xl font-bold text-foreground mb-1">Damage Assessment</h2>
            <p className="text-sm text-muted-foreground mb-4">Describe your situation and we'll respond fast. <Link to="/moisture-survey" className="text-wtr-blue hover:underline font-medium">What's a moisture survey?</Link></p>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <FormField control={form.control} name="name" render={({ field }) =>
                  <FormItem>
                        <FormLabel className="text-xs">Name *</FormLabel>
                        <FormControl>
                          <Input placeholder="Your name" className="h-9 text-sm" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                  } />
                  
                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) =>
                    <FormItem>
                        <FormLabel className="text-xs">Phone *</FormLabel>
                        <FormControl>
                          <Input type="tel" placeholder="(XXX) XXX-XXXX" className="h-9 text-sm" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    } />
                  
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <FormField
                    control={form.control}
                    name="zip"
                    render={({ field }) =>
                    <FormItem>
                        <FormLabel className="text-xs">ZIP Code *</FormLabel>
                        <FormControl>
                          <Input placeholder="78130" maxLength={5} className="h-9 text-sm" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    } />
                  
                  <FormField
                    control={form.control}
                    name="serviceType"
                    render={({ field }) =>
                    <FormItem>
                        <FormLabel className="text-xs">Service *</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger className="h-9 text-sm">
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="water-damage">Water Damage</SelectItem>
                            <SelectItem value="mold-inspection">Mold Inspection</SelectItem>
                            <SelectItem value="insurance">Insurance Help</SelectItem>
                            <SelectItem value="preventive">Preventive Eval</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    } />
                  
                </div>

                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) =>
                  <FormItem>
                      <FormLabel className="text-xs">Describe Your Situation *</FormLabel>
                      <FormControl>
                        <Textarea
                        placeholder="What happened? Where's the damage?"
                        rows={3}
                        className="text-sm resize-none"
                        {...field} />
                      
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  } />
                

                {/* Honeypot */}
                <div className="hidden" aria-hidden="true">
                  <input type="text" name="website" tabIndex={-1} autoComplete="off" />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
                  
                  {isSubmitting ? "Sending..." : "Request Moisture Survey"}
                </Button>
              </form>
            </Form>
          </div>
        </div>
      </div>
    </section>);

};